// A HID barcode scanner is a keyboard. Two things follow, and both are the
// reason this file exists rather than a plain v-model on a text input:
//
//   1. Under a Thai IME, event.key for the physical "A" key is "ฟ". event.code
//      is the physical key and never changes with layout, so we read that.
//   2. A scanner emits its whole payload in tens of milliseconds; a human does
//      not. Inter-key gap is what tells the two apart.

/** Longest plausible gap between two characters of one scan. Cheap scanners
 *  emit at 5-20ms; 60ms leaves headroom without reaching human typing speed
 *  (a fast typist sustains ~120ms between keys). */
export const MAX_SCAN_GAP_MS = 60;

/** Prefix (>=2) + 8 body + 1 check. Anything shorter cannot be a voucher code. */
export const MIN_SCAN_LENGTH = 11;

const KEY_RE = /^Key([A-Z])$/;
const DIGIT_RE = /^Digit([0-9])$/;

/**
 * Physical key code -> the character that key bears on a US layout, which is
 * what the scanner is emulating. Returns null for anything that is not part of
 * a code payload.
 * @param {string} code a KeyboardEvent.code value
 * @returns {string|null}
 */
export function charFromCode(code) {
  const letter = KEY_RE.exec(code);
  if (letter) return letter[1];
  const digit = DIGIT_RE.exec(code);
  if (digit) return digit[1];
  if (code === "Minus") return "-";
  return null;
}

export class ScanBuffer {
  constructor() {
    this.chars = [];
    this.lastTs = 0;
  }

  /**
   * @param {string} char one character from charFromCode
   * @param {number} ts event timestamp in ms
   */
  push(char, ts) {
    // A gap longer than a scanner can produce means this keystroke belongs to a
    // new burst, not the one in the buffer. Without this, a scan at 09:00 and a
    // scan at 09:05 would concatenate into one nonsense code.
    if (this.lastTs && ts - this.lastTs > MAX_SCAN_GAP_MS) {
      this.chars = [];
    }
    this.chars.push(char);
    this.lastTs = ts;
  }

  /**
   * Called on Enter. Returns the buffered payload if it looks like a scan, and
   * clears the buffer either way.
   * @param {number} ts timestamp of the Enter keystroke
   * @returns {string|null}
   */
  takeIfScan(ts) {
    const payload = this.chars.join("");
    const gapOk = !this.lastTs || ts - this.lastTs <= MAX_SCAN_GAP_MS;
    this.chars = [];
    this.lastTs = 0;
    if (!gapOk) return null;
    if (payload.length < MIN_SCAN_LENGTH) return null;
    return payload;
  }

  reset() {
    this.chars = [];
    this.lastTs = 0;
  }
}
